import type { FC } from 'react';
import React, { useCallback } from 'react';
import compose from 'compose-function';

import { daDataToFrontGenders, GENDER_UNKNOWN } from '../../config';

import type { TField } from './models';
import { defaultHandler } from './config';

export const Field: FC<TField> = ({
    Select,
    getCaption,
    isError,
    isSuccess,
    sendEvent,
    caption,
    onBlur,
    onChange,
    onClick,
    onFocus,
    onSelect = defaultHandler,
    addHiddenInput = false,
    disabled = false,
    forwardedRef,
    formatToRussian,
    replacePointSpaceToSpace,
    everyWordToUpperCase,
    deleteExcessSpaceAndDash,
    field,
    maxLength = 35,
    maxLengthField = 107,
    cutByLength,
    fieldAnalytics: analytics = {},
    options = [],
    placeholder,
    value: fieldValue = '',
    ...rest
}) => {
    const limitLength = useCallback(cutByLength(maxLength), [maxLength]);

    return (
        <Select
            {...rest}
            addHiddenInput={addHiddenInput}
            caption={getCaption(field.meta, caption)}
            disabled={disabled}
            error={isError(field.meta)}
            forwardedRef={forwardedRef}
            maxLength={maxLengthField}
            name={field.input.name}
            onBlur={({ event, name, value }) => {
                const trimmed = String(value).trim();

                field.input.onChange(trimmed);
                field.input.onBlur(event);
                if (onBlur) {
                    onBlur({ event, value: trimmed, name });
                }
            }}
            onChange={({ event, name, value }) => {
                const composeFunction = compose(
                    formatToRussian,
                    /*
                     * Мак при нажатии дабл-пробела ставит точку и пробел, которая потом форматируется
                     * в букву 'ю' (точка и 'ю' на одной клавише), поэтому заменяем просто на пробел
                     */
                    replacePointSpaceToSpace,
                    deleteExcessSpaceAndDash,
                    limitLength,
                    everyWordToUpperCase
                );

                const resultValue = composeFunction(String(value));

                field.input.onChange(resultValue);
                onChange({ event, name, value: resultValue });
            }}
            onClick={event => {
                if (Object.keys(analytics).length) sendEvent(analytics);
                if (onClick) {
                    onClick(event);
                }
            }}
            onFocus={({ event, name, value }) => {
                field.input.onFocus(event);
                if (onFocus) {
                    onFocus({ name, value: String(value).trim(), event });
                }
            }}
            onSelect={event => {
                field.input.onChange(event.value);
                field.input.onBlur();
                const { gender } = event;
                const newGender = gender ? daDataToFrontGenders[gender] : GENDER_UNKNOWN;

                onSelect({ ...event, field, gender: newGender });
            }}
            options={options}
            placeholder={placeholder}
            spellCheck={false}
            success={isSuccess(field.meta)}
            value={fieldValue}
        />
    );
};
